import React, { useState, useEffect } from 'react';
import toastService from '@/utils/toastService.js';

/**
 * Componente para mostrar una notificación toast
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.toast - Datos del toast
 * @param {function} props.onClose - Función al cerrar el toast
 * @returns {JSX.Element} - Componente Toast
 */
const Toast = ({ toast, onClose }) => {
  // Iconos y colores según el tipo
  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          bgColor: 'bg-green-500',
          textColor: 'text-white',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
      case 'error':
        return {
          bgColor: 'bg-red-500',
          textColor: 'text-white',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
      case 'warning':
        return {
          bgColor: 'bg-yellow-500',
          textColor: 'text-white',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )
        };
      case 'info':
      default:
        return {
          bgColor: 'bg-blue-500',
          textColor: 'text-white',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
    }
  };

  const { bgColor, textColor, icon } = getToastStyles();

  // Calcular el tiempo restante para la animación de progreso
  const [timeLeft, setTimeLeft] = useState(100);
  
  useEffect(() => {
    if (toast.duration > 0) {
      const interval = setInterval(() => {
        const elapsed = Date.now() - toast.timestamp;
        const remaining = Math.max(0, 100 - (elapsed / toast.duration) * 100);
        setTimeLeft(remaining);
        
        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 100);
      
      return () => clearInterval(interval);
    }
  }, [toast]);

  return (
    <div 
      className={`w-auto min-w-[250px] max-w-md ${bgColor} shadow-lg rounded-lg pointer-events-auto overflow-hidden`}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className={`flex-shrink-0 ${textColor}`}>
            {icon}
          </div>
          <div className="ml-3 flex-1">
            <p className={`text-base font-medium ${textColor} break-words`}>{toast.message}</p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className={`inline-flex ${textColor} focus:outline-none hover:bg-black hover:bg-opacity-10 p-1 rounded`}
              onClick={() => onClose(toast.id)}
            >
              <span className="sr-only">Cerrar</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Barra de progreso */}
      {toast.duration > 0 && (
        <div className="bg-black bg-opacity-10 h-1">
          <div 
            className="bg-white bg-opacity-30" 
            style={{ width: `${timeLeft}%`, height: '100%', transition: 'width 0.1s linear' }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Componente contenedor para las notificaciones toast
 * 
 * @returns {JSX.Element} - Componente ToastContainer
 */
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Suscribirse a cambios en las notificaciones
  useEffect(() => {
    const unsubscribe = toastService.subscribe(updatedToasts => {
      setToasts(updatedToasts);
    });
    
    return unsubscribe;
  }, []);
  
  // Eliminar una notificación
  const handleClose = (id) => {
    toastService.removeToast(id);
  };
  
  // Si no hay notificaciones, no renderizar nada
  if (toasts.length === 0) return null;
  
  // En móvil: arriba al centro, en escritorio: abajo a la derecha
  // z-[9999] para estar por encima de modales (z-50)
  const positionClasses = isMobile 
    ? "fixed top-4 inset-x-0 z-[9999] flex flex-col space-y-3 items-center px-4" 
    : "fixed bottom-4 right-4 z-[9999] flex flex-col space-y-3 items-end";
  
  // Animación diferente según dispositivo
  const animationClass = isMobile ? "animate-slideInDown" : "animate-slideInRight";
  
  return (
    <div className={positionClasses}>
      {toasts.map(toast => (
        <div 
          key={toast.id}
          className={`transform transition-all duration-300 ease-in-out 
                     ${animationClass} opacity-100 w-auto z-[9999]`}
        >
          <Toast toast={toast} onClose={handleClose} />
        </div>
      ))}
    </div>
  );
};

// Agregar animaciones personalizadas
const customStyles = `
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideInDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-slideInRight {
  animation: slideInRight 0.3s ease-out forwards;
}

.animate-slideInDown {
  animation: slideInDown 0.3s ease-out forwards;
}
`;

const ToastContainerWithStyles = () => (
  <>
    <style>{customStyles}</style>
    <ToastContainer />
  </>
);

export default ToastContainerWithStyles;